/**
 * ScopeStrip — the live summary of what the dashboard is showing (DSA
 * 1.4, prototype v7 "Vista: Por SKU · Categoría: Todas · …"): one chip
 * per report filter, "Label: value", built from the ACTIVE filters. An
 * empty filter reads "All"; an empty day reads "latest day with data".
 * Option values show their authored `option_labels`. Read-only: the
 * strip never writes a filter.
 *
 * The report page no longer draws it (encabezado compacto H1c,
 * 2026-10-26: the filter bar shows the same values, and ``scope_strip``
 * is retired from configs). It stays for the read-only chips of a send
 * capture, where there is no filter bar.
 */
import { useTranslation } from "react-i18next";

import { isLatestDay, skuListCount } from "./filter-format";
import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import { useLang } from "./i18n-binding";
import type { DashboardFilter } from "./types";

export interface ScopeStripProps {
  filters: ReadonlyArray<DashboardFilter>;
  activeFilters: Readonly<Record<string, string>>;
}

function selection(raw: string): string[] {
  if (raw.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      /* not a JSON list: a plain value that starts with "[" */
    }
  }
  return [raw];
}

export function ScopeStrip({
  filters,
  activeFilters,
}: ScopeStripProps): JSX.Element | null {
  const { t } = useTranslation();
  const lang = useLang();
  if (filters.length === 0) return null;
  return (
    <div
      data-scope-strip=""
      role="list"
      aria-label={t("dashboard.scope.aria")}
      className="flex flex-wrap gap-1.5"
    >
      {filters.map((f) => {
        const label = resolveLocalized(f.label as DisplayLabel, lang) || f.key;
        const raw = activeFilters[f.key] ?? f.locked ?? f.default_value ?? "";
        // A ``"latest"`` day reads like the empty one: the latest day.
        const values =
          raw === "" || (f.type === "date_calendar" && isLatestDay(raw)) ? [] : selection(raw);
        const shown =
          values.length === 0
            ? f.type === "date_calendar"
              ? t("dashboard.scope.latest")
              : t("dashboard.scope.all")
            : f.type === "sku_list"
              ? // DSA "Filtros guardados": the value is a reference to the
                // saved list ({"saved": id, "n": N}); say how many codes,
                // never the reference nor the codes.
                skuListCount(raw) === null
                ? t("filterbar.skuList")
                : t("filterbar.skuListCodes", { n: skuListCount(raw) ?? 0 })
              : values
                  .map((v) => resolveLocalized(f.option_labels?.[v] as DisplayLabel, lang) || v)
                  .join(", ");
        return (
          <span
            key={f.key}
            role="listitem"
            data-scope-chip={f.key}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-[3px] text-2xs leading-4 text-neutral-700 tabular-nums"
          >
            <span className="text-neutral-500">{label}:</span>
            <span className="font-semibold">{shown}</span>
          </span>
        );
      })}
    </div>
  );
}
